// client/src/api/services/kxtService.ts
import { apiClient } from '../client';
import { ENDPOINTS, buildQueryString } from '../endpoints';

export interface KXTWallet {
  hasKxtWallet: boolean;
  kxtWalletAddress: string | null;
  kxtBalanceLastSynced: string | null;
}

export interface KXTBalance {
  kxtBalance: number;
  kxtPendingWithdrawal: number;
  kxtTotalEarned: number;
  kxtTotalWithdrawn: number;
  kxtWalletAddress: string | null;
  onChainBalance: string | null;
  kxtBalanceLastSynced: string | null;
}

export interface RewardScore {
  hotspot: unknown;
  period: string;
  periodStart: string;
  periodEnd: string;
  rewardScore: number;
  rewardPercentage: number;
  finalRewardKXT: number;
  rank: number;
  metrics: {
    connectedUsers:  { weight: string; value: number; normalizedScore: number };
    planTier:        { weight: string; value: number; normalizedScore: number };
    connectionSpeed: { weight: string; value: string; normalizedScore: number };
    uptime:          { weight: string; value: string; normalizedScore: number };
    streak:          { weight: string; currentStreak: number; longestStreak: number; normalizedScore: number };
    referrals:       { weight: string; value: number; normalizedScore: number };
  } | null;
  message?: string;
}

export interface StreakData {
  currentStreak: number;
  longestStreak: number;
  streakBonus: string;
  nextMilestone: { days: number; bonus: string; daysLeft?: number } | null;
}

export interface LeaderboardEntry {
  operator: { name: string; profilePhoto?: string };
  hotspot: { name: string; location: unknown };
  rewardScore: number;
  rewardPercentage: number;
  finalRewardKXT: number;
  rank: number;
  currentStreak: number;
}

export interface LeaderboardData {
  period: string;
  leaderboard: LeaderboardEntry[];
  myRank: number | null;
  myScore: number | null;
  myReward: number | null;
}

export interface KXTTransaction {
  _id: string;
  type: string;
  amount: number;
  currency: string;
  description: string;
  status: string;
  createdAt: string;
  kxt?: { txHash?: string; walletAddress?: string };
}

export interface WithdrawalRequest {
  _id: string;
  amount: number;
  currency: string;
  withdrawalMethod: string;
  withdrawalDetails: unknown;
  status: 'pending' | 'processing' | 'completed' | 'rejected';
  netAmount: number;
  fee: number;
  createdAt: string;
  metadata?: unknown;
}

export interface WithdrawalPagination {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface SubmitWithdrawalPayload {
  amount: number;
  withdrawalMethod: 'bank_transfer' | 'mobile_money' | 'crypto' | 'mobile_mobile';
  withdrawalDetails: {
    bankName?: string;
    accountName?: string;
    bankNumber?: string;
    phoneNumber?: string;
    walletAddress?: string;
    [key: string]: unknown;
  };
}

export interface ConvertKxtToUsdResponse {
  usd: number;
}

export interface ConvertUsdToKxtResponse {
  kxt: number;
}

export const kxtService = {

  getWallet: async (): Promise<KXTWallet> => {
    const res = await apiClient.get<{ success: boolean; data: KXTWallet }>(ENDPOINTS.OPERATOR_KXT.WALLET);
    return res.data.data;
  },

  generateWallet: async (): Promise<{ kxtWalletAddress: string; network: string; chainId: number }> => {
    const res = await apiClient.post<{ success: boolean; data: { kxtWalletAddress: string; network: string; chainId: number } }>(ENDPOINTS.OPERATOR_KXT.WALLET_GENERATE, {});
    return res.data.data;
  },

  getBalance: async (): Promise<KXTBalance> => {
    const res = await apiClient.get<{ success: boolean; data: KXTBalance }>(ENDPOINTS.OPERATOR_KXT.BALANCE);
    return res.data.data;
  },

  getRewardScore: async (): Promise<RewardScore> => {
    const res = await apiClient.get<{ success: boolean; data: RewardScore }>(ENDPOINTS.OPERATOR_KXT.REWARD_SCORE);
    return res.data.data;
  },

  getStreak: async (): Promise<StreakData> => {
    const res = await apiClient.get<{ success: boolean; data: StreakData }>(ENDPOINTS.OPERATOR_KXT.STREAK);
    return res.data.data;
  },

  getLeaderboard: async (period: 'daily' | 'weekly' | 'monthly' = 'daily'): Promise<LeaderboardData> => {
    const res = await apiClient.get<{ success: boolean; data: LeaderboardData }>(
      `${ENDPOINTS.OPERATOR_KXT.LEADERBOARD}${buildQueryString({ period })}`
    );
    return res.data.data;
  },

  getEarningsHistory: async (page = 1, limit = 20): Promise<{ transactions: KXTTransaction[]; pagination: unknown }> => {
    const res = await apiClient.get<{ success: boolean; data: { transactions: KXTTransaction[]; pagination: unknown } }>(
      `${ENDPOINTS.OPERATOR_KXT.EARNINGS_HISTORY}${buildQueryString({ page, limit })}`
    );
    return res.data.data;
  },

  getTransactions: async (page = 1, limit = 20): Promise<{ transactions: KXTTransaction[]; pagination: unknown }> => {
    const res = await apiClient.get<{ success: boolean; data: { transactions: KXTTransaction[]; pagination: unknown } }>(
      `${ENDPOINTS.OPERATOR_KXT.TRANSACTIONS}${buildQueryString({ page, limit })}`
    );
    return res.data.data;
  },

  getKxtUsdRate: async (): Promise<{ kxt: number; usd: number; usdPerKxt: number; rate?: number; formula?: string }> => {
    const res = await apiClient.get<{ success: boolean; data: { kxt: number; usd: number; usdPerKxt: number; rate?: number; formula?: string } }>(ENDPOINTS.OPERATOR_KXT.RATE_USD);
    return res.data.data;
  },

  convertUsd: async (kxt: number): Promise<ConvertKxtToUsdResponse> => {
    const res = await apiClient.post<{ success?: boolean; data?: unknown; usd?: number }>(
      ENDPOINTS.OPERATOR_KXT.CONVERT_USD,
      { kxt }
    );
    const payload = res.data?.data ?? res.data ?? {};
    const usd = (payload as unknown as Record<string, unknown>).usd ?? (payload as unknown as Record<string, unknown>).convertedUsd ?? (payload as unknown as Record<string, unknown>).amountInUsd ?? (payload as unknown as Record<string, unknown>).valueUsd;

    if (!Number.isFinite(Number(usd))) {
      throw new Error('Invalid USD conversion response');
    }

    return { usd: Number(usd) };
  },

  convertKxtFromUsd: async (usd: number): Promise<ConvertUsdToKxtResponse> => {
    const res = await apiClient.post<{ success?: boolean; data?: unknown; kxt?: number }>(
      ENDPOINTS.OPERATOR_KXT.CONVERT_KXT,
      { usd }
    );
    const payload = res.data?.data ?? res.data ?? {};
    const kxt = (payload as unknown as Record<string, unknown>).kxt ?? (payload as unknown as Record<string, unknown>).convertedKxt ?? (payload as unknown as Record<string, unknown>).amountInKxt ?? (payload as unknown as Record<string, unknown>).valueKxt;

    if (!Number.isFinite(Number(kxt))) {
      throw new Error('Invalid KXT conversion response');
    }

    return { kxt: Number(kxt) };
  },

  submitWithdrawal: async (payload: SubmitWithdrawalPayload): Promise<{
    withdrawalId: string;
    amount: number;
    currency: string;
    netAmount: number;
    status: string;
    withdrawalMethod: string;
  }> => {
    const res = await apiClient.post<{ success: boolean; data: {
      withdrawalId: string;
      amount: number;
      currency: string;
      netAmount: number;
      status: string;
      withdrawalMethod: string;
    } }>(ENDPOINTS.OPERATOR_KXT.WITHDRAW, payload);
    return res.data.data;
  },

  getWithdrawals: async (
    page = 1,
    limit = 20,
    status?: string
  ): Promise<{ withdrawals: WithdrawalRequest[]; pagination: WithdrawalPagination }> => {
    const params: Record<string, unknown> = { page, limit };
    if (status) params.status = status;
    const res = await apiClient.get<{ success: boolean; data: Record<string, unknown> }>(
      `${ENDPOINTS.OPERATOR_KXT.WITHDRAWALS}${buildQueryString(params)}`
    );
    const payload = res.data.data || {} as unknown as Record<string, unknown>;
    const paginationPayload = (payload.pagination || {}) as unknown as Record<string, unknown>;
    const currentPage = (paginationPayload.currentPage || paginationPayload.page || page) as number;
    const itemsPerPage = (paginationPayload.itemsPerPage || paginationPayload.limit || limit) as number;
    const totalItems = ((paginationPayload.totalItems || paginationPayload.total || (payload.withdrawals as unknown[] | undefined)?.length || 0)) as number;
    const totalPages = (
      paginationPayload.totalPages ||
      paginationPayload.pages ||
      Math.max(1, Math.ceil(totalItems / Math.max(itemsPerPage, 1)))) as number;

    return {
      withdrawals: (payload.withdrawals || []) as WithdrawalRequest[],
      pagination: {
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        hasNext: (paginationPayload.hasNext ?? paginationPayload.hasMore ?? currentPage < totalPages) as boolean,
        hasPrevious: (paginationPayload.hasPrevious ?? currentPage > 1) as boolean,
      },
    };
  },
};

export default kxtService;
